import React, { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  CoworkerGroupBuilderBody,
  CoworkerGroupBuilderFooter,
  CoworkerGroupBuilderProvider,
  CoworkerGroupBuilderState,
} from './coworker-group-builder';
import { CoworkerGroupChips } from './coworker-group-chips';
import { CoworkerGroupDeleteDialog } from './coworker-group-delete-dialog';
import { CoworkerGroupActionsMenu } from './coworker-group-menu';
import { CloseIcon, SearchIcon } from './icons';
import {
  CoworkerAvatar,
  CoworkerBlockedNotice,
  CoworkerEmptyState,
  CoworkerFormError,
  CoworkerList,
  CoworkerPanel,
  CoworkerPanelFooter,
  CoworkerRow,
  CoworkerRowName,
  CoworkerRowSubtitle,
  CoworkerRowText,
  CoworkerSearchField,
  CoworkerSearchInput,
  CoworkerSheet,
  CoworkerSheetFooter,
  CoworkerShowMoreButton,
  CoworkerSummary,
  CoworkerSummaryActions,
  CoworkerSummaryCount,
  CoworkerSummaryCountButton,
  CoworkerSummaryText,
  CoworkerViewButton,
  FiltersBody,
  FiltersCloseButton,
  FiltersOverlay,
  FiltersPanelHeader,
  FiltersPanelTitle,
  FiltersSheetHeader,
  FiltersSheetTitle,
} from './styles';
import { ModalPortal } from 'components/modals/modal-portal/modal-portal';
import { useActiveCoworkerGroup } from '../hooks/use-active-coworker-group.hook';
import { useDialogBehavior } from '../hooks/use-dialog-behavior.hook';
import { useCoworkerJump, isJumpable } from '../hooks/use-coworker-jump.hook';
import { useFloorplanMapDate } from '../hooks/use-floorplan-map-date.hook';
import { useCoworkerGroups } from '../hooks/use-coworker-groups.hook';
import { useCoworkerGroupMutations } from 'data/mutations/useCoworkerGroupMutations.hook';
import { useCoworkerSearchQuery } from 'data/queries/useCoworkerSearchQuery.hook';
import { COWORKER_BLOCKED_STATE_MESSAGE_ID, useCoworkerVisibility } from 'floorplan/hooks/use-coworker-visibility.hook';
import { useDebouncedValue } from 'hooks/use-debounced-value.hook';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { CoworkerResponse } from 'store/api-client/coworker-types';

interface CoworkerSearchSheetProps {
  onClose: VoidFunction;
}

interface CoworkerRowItemProps {
  coworker: CoworkerResponse;
  subtitle: string;
  onView: (coworker: CoworkerResponse) => void;
}

/** One directory row; "View" is present only when there is a placed desk to jump to. */
const CoworkerRowItem = ({ coworker, subtitle, onView }: CoworkerRowItemProps): JSX.Element => {
  const intl = useIntl();
  const handleView = useCallback(() => onView(coworker), [onView, coworker]);

  return (
    <CoworkerRow data-testid={`coworker-row-${coworker.userUuid}`}>
      <CoworkerAvatar aria-hidden>{coworker.initials}</CoworkerAvatar>
      <CoworkerRowText>
        <CoworkerRowName>{coworker.name}</CoworkerRowName>
        <CoworkerRowSubtitle data-testid={`coworker-subtitle-${coworker.userUuid}`}>{subtitle}</CoworkerRowSubtitle>
      </CoworkerRowText>
      {isJumpable(coworker) && (
        <CoworkerViewButton
          type="button"
          data-testid={`coworker-view-${coworker.userUuid}`}
          aria-label={intl.formatMessage({ id: 'viewCoworkerOnMap' }, { name: coworker.name })}
          onClick={handleView}
        >
          {intl.formatMessage({ id: 'view' })}
        </CoworkerViewButton>
      )}
    </CoworkerRow>
  );
};

const TITLE_ID = 'coworker-search-title';
const SEARCH_DEBOUNCE_MS = 250;

/**
 * "Find a coworker" (§C2) with groups (§C3): search the requester's visible coworkers by name, see
 * where each one sits that day, and jump to the desk; save the people you sit near as a group, and
 * focus the map on one (the active group — the map's focus mode, read from the url). A bottom sheet on
 * mobile, a centred panel on the web — the filters sheet's shell, so the two surfaces the map opens
 * look alike.
 *
 * Rows are inert unless the coworker sits on a placed desk that day AND names are revealed — the
 * "View" is the only interactive part, so an inert row simply has none. With a group active the rows
 * are its members (of the loaded pages), and the summary counts them.
 *
 * Search requires names (§0.1 effectiveName); groups also require teams (effectiveTeams) — with teams
 * off the chips and the builder are simply absent and search stays. The entry point is hidden while
 * the whole coworker layer is off, so the blocked copy shown here is the company-only case.
 */
export const CoworkerSearchSheet = ({ onClose }: CoworkerSearchSheetProps): JSX.Element => {
  const intl = useIntl();
  const isMobileDevice = useIsSmallViewportWidth();
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { effectiveName, blockedState } = useCoworkerVisibility();
  const { date, getToday } = useFloorplanMapDate();
  const [search, setSearch] = useState('');
  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value), []);
  const query = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const jumpTo = useCoworkerJump();

  const { coworkers, total, hasMore, loadMore, isLoadingMore, isLoading, isError } = useCoworkerSearchQuery({
    query,
    date,
    enabled: effectiveName,
  });

  // Groups (§C3): the same resolution the map, the legend and the Coworkers button use.
  const { hasGroups, groups, activeGroup, isError: isGroupsError } = useCoworkerGroups();
  const { activeGroupUuid, setActiveGroup, clearActiveGroup, showGroupOnMap } = useActiveCoworkerGroup();
  const { deleteGroup, isDeleting } = useCoworkerGroupMutations();
  const [builder, setBuilder] = useState<CoworkerGroupBuilderState | null>(null);
  const [hasDeleteFailed, setHasDeleteFailed] = useState(false);

  const openCreate = useCallback(() => setBuilder({ mode: 'create' }), []);
  const openEdit = useCallback(() => {
    if (activeGroup) {
      setBuilder({ mode: 'edit', group: activeGroup });
    }
  }, [activeGroup]);
  const closeBuilder = useCallback(() => setBuilder(null), []);
  const handleShowGroup = useCallback(() => {
    if (activeGroup) {
      showGroupOnMap(activeGroup.uuid);
    }
  }, [activeGroup, showGroupOnMap]);

  // Deleting the group the map is focused on also clears the focus (CV-TEAM-06). The comparison is
  // made when the delete COMPLETES, against the focus as it is then — not as it was when Delete
  // was chosen — so a focus moved meanwhile is still judged correctly. (The chips are inert
  // during the delete, so this is defence in depth against a url change from elsewhere.)
  const activeGroupUuidRef = useRef(activeGroupUuid);
  const clearActiveGroupRef = useRef(clearActiveGroup);

  useEffect(() => {
    activeGroupUuidRef.current = activeGroupUuid;
    clearActiveGroupRef.current = clearActiveGroup;
  }, [activeGroupUuid, clearActiveGroup]);

  const handleDelete = useCallback(
    async (groupUuid: string) => {
      setHasDeleteFailed(false);

      try {
        await deleteGroup(groupUuid);

        if (groupUuid === activeGroupUuidRef.current) {
          clearActiveGroupRef.current();
        }
      } catch {
        setHasDeleteFailed(true);
      }
    },
    [deleteGroup],
  );

  // The menu's Delete only asks; the request goes out when the confirmation is accepted. The group
  // is recorded when the question is asked, so a focus that moves to another group while the dialog
  // is open (a url change) cannot redirect the delete.
  const [pendingDeleteUuid, setPendingDeleteUuid] = useState<string | null>(null);
  const deleteTriggerRef = useRef<HTMLElement | null>(null);
  const activeGroupUuidForDelete = activeGroup?.uuid ?? null;
  const askToDeleteActiveGroup = useCallback(
    (trigger: HTMLButtonElement | null) => {
      deleteTriggerRef.current = trigger;
      setPendingDeleteUuid(activeGroupUuidForDelete);
    },
    [activeGroupUuidForDelete],
  );
  const cancelDelete = useCallback(() => setPendingDeleteUuid(null), []);
  const confirmDelete = useCallback(() => {
    setPendingDeleteUuid(null);

    if (pendingDeleteUuid) {
      handleDelete(pendingDeleteUuid);
    }
  }, [pendingDeleteUuid, handleDelete]);

  // The group's menu is the only thing that held the focus while its group was being deleted; once the
  // group (and so the menu) is gone the focus would fall to the page, so it goes back to the search field.
  useEffect(() => {
    if (!activeGroup && document.activeElement === document.body) {
      searchInputRef.current?.focus();
    }
  }, [activeGroup]);

  // Escape closes the builder first, then the sheet — one layer at a time, like the back button would.
  useDialogBehavior(dialogRef, builder ? closeBuilder : onClose);

  const isToday = date === getToday();
  const formattedDate = useMemo(
    () => intl.formatDate(new Date(`${date}T00:00:00`), { day: 'numeric', month: 'short' }),
    [intl, date],
  );

  // With a group active the list is its members — of the loaded pages; more may sit past "show more".
  const rows = useMemo(
    () =>
      activeGroup ? coworkers.filter(coworker => activeGroup.memberUserUuids.includes(coworker.userUuid)) : coworkers,
    [coworkers, activeGroup],
  );
  // The summary's count toggles the booked rows to the top; the sort is stable, so each half keeps
  // the directory's order.
  const [isBookedFirst, setIsBookedFirst] = useState(false);
  const toggleBookedFirst = useCallback(() => setIsBookedFirst(value => !value), []);
  const sortedRows = useMemo(
    () => (isBookedFirst ? [...rows].sort((a, b) => Number(!!b.booking) - Number(!!a.booking)) : rows),
    [rows, isBookedFirst],
  );
  // Both sides of the summary are the LOADED rows: the BE's total counts matches past the current
  // page too, whose bookings are unknown until they are loaded — the show-more control names that.
  const bookedCount = rows.filter(coworker => !!coworker.booking).length;
  const loadedCount = coworkers.length;

  // The `<count>` rich-text tag of the summary messages: "3 of 7" set apart from the rest of the line,
  // and the sort toggle once anyone is booked.
  const highlightCount = (chunks: ReactNode[]): JSX.Element =>
    bookedCount > 0 ? (
      <CoworkerSummaryCountButton
        type="button"
        data-testid="coworker-search-booked-first"
        aria-pressed={isBookedFirst}
        title={intl.formatMessage({ id: 'showBookedCoworkersFirst' })}
        onClick={toggleBookedFirst}
      >
        {chunks}
      </CoworkerSummaryCountButton>
    ) : (
      <CoworkerSummaryCount>{chunks}</CoworkerSummaryCount>
    );

  const subtitleFor = (coworker: CoworkerResponse): string => {
    if (!coworker.booking) {
      return isToday
        ? intl.formatMessage({ id: 'notBookedToday' })
        : intl.formatMessage({ id: 'notBookedOnDate' }, { date: formattedDate });
    }

    const { resourceName, floorLabel } = coworker.booking;

    return floorLabel ? `${resourceName} · ${floorLabel}` : resourceName;
  };

  const dialogProps = {
    ref: dialogRef,
    'data-testid': 'coworker-search-sheet',
    role: 'dialog',
    'aria-modal': true,
    'aria-labelledby': TITLE_ID,
    tabIndex: -1,
  };

  const closeButton = (
    <FiltersCloseButton
      type="button"
      data-testid="coworker-search-close"
      aria-label={intl.formatMessage({ id: 'close' })}
      onClick={builder ? closeBuilder : onClose}
    >
      <CloseIcon />
    </FiltersCloseButton>
  );

  const groupMenu = (
    <CoworkerGroupActionsMenu isDeleting={isDeleting} onEdit={openEdit} onDelete={askToDeleteActiveGroup} />
  );

  // With nothing to summarise (a search that matches none of the members, a group without loaded
  // members, or a directory error that hides the summary even though rows are retained) the summary is not rendered, but Edit and Delete must stay reachable — the chip's old
  // × never depended on the results.
  const groupOnlyBar = activeGroup && (
    <CoworkerSummary data-testid="coworker-group-bar">
      <CoworkerSummaryText>{activeGroup.name}</CoworkerSummaryText>
      <CoworkerSummaryActions>{groupMenu}</CoworkerSummaryActions>
    </CoworkerSummary>
  );

  const summary = (
    <CoworkerSummary data-testid="coworker-search-summary">
      <CoworkerSummaryText>
        {isToday
          ? intl.formatMessage(
              { id: 'coworkersBookedToday' },
              { booked: bookedCount, total: rows.length, count: highlightCount },
            )
          : intl.formatMessage(
              { id: 'coworkersBookedOnDate' },
              { booked: bookedCount, total: rows.length, date: formattedDate, count: highlightCount },
            )}
      </CoworkerSummaryText>
      {activeGroup && (
        <CoworkerSummaryActions>
          <CoworkerViewButton
            type="button"
            data-testid="coworker-group-view"
            aria-label={intl.formatMessage({ id: 'showGroupOnMap' }, { name: activeGroup.name })}
            onClick={handleShowGroup}
          >
            {intl.formatMessage({ id: 'view' })}
          </CoworkerViewButton>
          {groupMenu}
        </CoworkerSummaryActions>
      )}
    </CoworkerSummary>
  );

  const searchBody = (
    <>
      <CoworkerSearchField>
        <SearchIcon />
        <CoworkerSearchInput
          ref={searchInputRef}
          type="text"
          data-testid="coworker-search-input"
          placeholder={intl.formatMessage({ id: 'searchByName' })}
          aria-label={intl.formatMessage({ id: 'searchByName' })}
          value={search}
          autoFocus
          onChange={handleSearchChange}
        />
      </CoworkerSearchField>
      {hasGroups && (
        <>
          <CoworkerGroupChips
            groups={groups}
            activeGroupUuid={activeGroup?.uuid ?? null}
            isDeleting={isDeleting}
            onSelect={setActiveGroup}
            onCreate={openCreate}
          />
          {isGroupsError && (
            <CoworkerFormError data-testid="coworker-groups-error">
              {intl.formatMessage({ id: 'coworkerGroupsFailed' })}
            </CoworkerFormError>
          )}
          {hasDeleteFailed && (
            <CoworkerFormError role="alert" data-testid="coworker-group-delete-error">
              {intl.formatMessage({ id: 'coworkerGroupDeleteFailed' })}
            </CoworkerFormError>
          )}
        </>
      )}
      {isError && (
        <CoworkerEmptyState data-testid="coworker-search-error">
          <strong>{intl.formatMessage({ id: 'coworkerSearchFailed' })}</strong>
        </CoworkerEmptyState>
      )}
      {(isError || rows.length === 0) && groupOnlyBar}
      {!isError && !isLoading && rows.length === 0 && (
        <CoworkerEmptyState data-testid="coworker-search-empty">
          <strong>{intl.formatMessage({ id: 'noCoworkersFound' })}</strong>
          <span>{intl.formatMessage({ id: hasGroups ? 'tryAnotherNameOrGroup' : 'tryAnotherName' })}</span>
        </CoworkerEmptyState>
      )}
      {!isError && rows.length > 0 && (
        <>
          {summary}
          <CoworkerList data-testid="coworker-search-results" aria-busy={isLoading}>
            {sortedRows.map(coworker => (
              <CoworkerRowItem
                key={coworker.userUuid}
                coworker={coworker}
                subtitle={subtitleFor(coworker)}
                onView={jumpTo}
              />
            ))}
          </CoworkerList>
        </>
      )}
      {!isError && hasMore && (
        <CoworkerShowMoreButton
          type="button"
          data-testid="coworker-search-more"
          disabled={isLoadingMore}
          onClick={loadMore}
        >
          {intl.formatMessage({ id: 'showMoreCoworkers' }, { shown: loadedCount, total })}
        </CoworkerShowMoreButton>
      )}
    </>
  );

  const renderBody = (): JSX.Element => {
    if (!effectiveName) {
      return (
        <CoworkerBlockedNotice data-testid="coworker-search-blocked">
          <strong>{intl.formatMessage({ id: 'coworkerBookingsHidden' })}</strong>
          <span>{intl.formatMessage({ id: COWORKER_BLOCKED_STATE_MESSAGE_ID[blockedState ?? 'visibilityOff'] })}</span>
        </CoworkerBlockedNotice>
      );
    }

    if (builder) {
      return <CoworkerGroupBuilderBody />;
    }

    return searchBody;
  };

  const body = renderBody();
  // The builder's Save / Cancel stay pinned under the scrolling fields (prototype: buttons fixed at the
  // sheet's bottom), so they render outside the body, in the shell's footer slot.
  const isBuilderOpen = !!builder && effectiveName;

  const titleId = (): string => {
    if (!builder) {
      return 'findCoworker';
    }

    return builder.mode === 'edit' ? 'editCoworkerGroup' : 'newCoworkerGroup';
  };

  const title = intl.formatMessage({ id: titleId() });

  // One fixed height for Find / New group / Edit group (`CoworkerSheet` / `CoworkerPanel`), so the
  // surface does not jump when the view switches — the body scrolls instead.
  const shell = isMobileDevice ? (
    <CoworkerSheet {...dialogProps}>
      <FiltersSheetHeader>
        <FiltersSheetTitle id={TITLE_ID}>{title}</FiltersSheetTitle>
        {closeButton}
      </FiltersSheetHeader>
      <FiltersBody>{body}</FiltersBody>
      {isBuilderOpen && (
        <CoworkerSheetFooter>
          <CoworkerGroupBuilderFooter />
        </CoworkerSheetFooter>
      )}
    </CoworkerSheet>
  ) : (
    <CoworkerPanel {...dialogProps}>
      <FiltersPanelHeader>
        <FiltersPanelTitle id={TITLE_ID}>{title}</FiltersPanelTitle>
        {closeButton}
      </FiltersPanelHeader>
      <FiltersBody hasPanelPadding>{body}</FiltersBody>
      {isBuilderOpen && (
        <CoworkerPanelFooter>
          <CoworkerGroupBuilderFooter />
        </CoworkerPanelFooter>
      )}
    </CoworkerPanel>
  );

  return (
    <ModalPortal>
      {pendingDeleteUuid && (
        <CoworkerGroupDeleteDialog
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
          returnFocusRef={deleteTriggerRef}
          fallbackFocusRef={dialogRef}
        />
      )}
      <FiltersOverlay data-testid="coworker-search-overlay" onClick={onClose} />
      {isBuilderOpen && builder ? (
        <CoworkerGroupBuilderProvider state={builder} date={date} onCancel={closeBuilder} onSaved={closeBuilder}>
          {shell}
        </CoworkerGroupBuilderProvider>
      ) : (
        shell
      )}
    </ModalPortal>
  );
};
